/* ---------------------------------------------------------------------------
   Palpa Impact Carousel — one initiative at a time.

   Left panel: a vertical wheel of pills that always brings the active one to
   the centre. Right panel: a deck of images where the active card sits flat
   and its two neighbours peek out behind it, slightly rotated.

   Everything derives from a single `index`. The item count is whatever the
   merchant added in the theme editor, so all the maths wraps around the ring —
   adding or removing a block needs no other change.

   The markup renders fine without this script (first card visible, pills in a
   plain column); `is-ready` is what switches on the stacked layout.
   --------------------------------------------------------------------------- */
(() => {
  const CHIP_GAP = 12; // px between pills in the wheel
  const CHIP_FADE = 2; // how many pills each side of the active one stay visible
  const CARD_OFFSET = 24; // % that the previous/next cards peek out by
  const CARD_TILT = 3; // deg of rotation on those neighbours

  /* Shortest signed distance from `from` to `to` on a ring of `count`. */
  const ringDistance = (from, to, count) => {
    const half = count / 2;
    let d = to - from;
    if (d > half) d -= count;
    if (d < -half) d += count;
    return d;
  };

  class PalpaImpactCarousel extends HTMLElement {
    connectedCallback() {
      this.chips = [...this.querySelectorAll('.palpa-impact__chip')];
      this.cards = [...this.querySelectorAll('.palpa-impact__card')];
      this.count = this.chips.length;
      if (!this.count) return;

      this.index = 0;
      this.paused = false;
      this.timer = null;
      this.chipOffsets = new Array(this.count).fill(null); // last rendered ring position
      this.interval = parseInt(this.dataset.interval, 10) || 4000;

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplay = this.dataset.autoplay === 'true' && this.count > 1 && !reduceMotion;

      this.onEnter = () => {
        this.paused = true;
      };
      this.onLeave = () => {
        this.paused = false;
      };
      this.onResize = () => this.render();
      this.onBlockSelect = (event) => this.selectBlock(event);
      this.onBlockDeselect = (event) => {
        if (this.contains(event.target)) this.start();
      };

      this.chips.forEach((chip) => {
        chip.addEventListener('click', () => {
          this.goTo(parseInt(chip.dataset.index, 10));
          this.start(); // restart the clock so the click gets a full turn
        });
      });

      this.addEventListener('mouseenter', this.onEnter);
      this.addEventListener('mouseleave', this.onLeave);
      this.addEventListener('focusin', this.onEnter);
      this.addEventListener('focusout', this.onLeave);
      window.addEventListener('resize', this.onResize);

      // Theme editor: picking a block in the sidebar should bring it forward.
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      this.classList.add('is-ready');
      this.render();

      /* The clock only runs while the deck is on screen. Started at load, it had
         already advanced two or three turns by the time the visitor scrolled this
         far down, so they arrived mid-sequence instead of at the first initiative.
         Re-entering restarts the turn rather than resuming a part-spent one. */
      this.observer = new IntersectionObserver(
        ([entry]) => (entry.isIntersecting ? this.start() : this.stop()),
        { threshold: 0.35 }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.stop();
      this.observer?.disconnect();
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    selectBlock(event) {
      if (!this.contains(event.target)) return;
      const chip = this.querySelector(`[data-block-id="${event.target.id}"]`);
      if (!chip) return;
      this.goTo(parseInt(chip.dataset.index, 10));
      this.stop(); // hold still while the merchant edits that block
    }

    goTo(index) {
      if (Number.isNaN(index)) return;
      this.index = ((index % this.count) + this.count) % this.count;
      this.render();
    }

    start() {
      this.stop();
      if (!this.autoplay) return;
      this.timer = setInterval(() => {
        if (!this.paused && !document.hidden) this.goTo(this.index + 1);
      }, this.interval);
    }

    stop() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }

    render() {
      const { count, index } = this;
      const step = this.chips[0].offsetHeight + CHIP_GAP;

      // The ring has a seam: the pill furthest from the active one is the same
      // pill whether you count up or down, so on the next turn it swaps ends.
      // `seam` is that furthest distance; anything at or past it is invisible,
      // which is what lets us move it without the eye catching the jump.
      const seam = Math.floor(count / 2);
      // With four or more pills the seam always falls outside the visible band,
      // so the swap happens at zero opacity. Two or three pills never travel far
      // enough for that to be worth hiding, so they all stay on screen.
      const fade = count > 3 ? Math.min(seam, CHIP_FADE + 1) : count;

      this.chips.forEach((chip, i) => {
        const d = ringDistance(index, i, count);
        const active = i === index;
        const distance = Math.abs(d);
        const hidden = distance >= fade;
        const opacity = hidden ? 0 : 1 - (distance / fade) * 0.75;

        const previous = this.chipOffsets[i];
        const jumped = previous !== null && Math.abs(d - previous) > seam;
        this.chipOffsets[i] = d;

        if (jumped) {
          // Relocate across the seam with no animation, and while invisible, so
          // the pill reappears at the other end instead of sliding past all the
          // others. The real opacity is applied afterwards and still animates.
          chip.style.transition = 'none';
          chip.style.opacity = 0;
          chip.style.transform = `translateY(${d * step}px)`;
          void chip.offsetHeight; // flush before the transition comes back
          chip.style.transition = '';
        } else {
          chip.style.transform = `translateY(${d * step}px)`;
        }

        chip.style.opacity = opacity;
        chip.style.pointerEvents = hidden ? 'none' : 'auto';
        chip.setAttribute('aria-selected', active ? 'true' : 'false');
        chip.setAttribute('tabindex', active ? '0' : '-1');
      });

      this.cards.forEach((card, i) => {
        const d = ringDistance(index, i, count);
        const neighbour = d === -1 || d === 1;

        const x = neighbour ? d * CARD_OFFSET : 0;
        const rotate = neighbour ? d * CARD_TILT : 0;
        const scale = d === 0 ? 1 : neighbour ? 0.85 : 0.7;
        const opacity = d === 0 ? 1 : neighbour ? 0.55 : 0;

        card.style.transform = `translateX(${x}%) scale(${scale}) rotate(${rotate}deg)`;
        card.style.opacity = opacity;
        card.style.zIndex = d === 0 ? 20 : neighbour ? 10 : 0;
        card.style.pointerEvents = d === 0 ? 'auto' : 'none';
        card.classList.toggle('is-active', d === 0);
        card.setAttribute('aria-hidden', d === 0 ? 'false' : 'true');
      });
    }
  }

  if (!customElements.get('palpa-impact-carousel')) {
    customElements.define('palpa-impact-carousel', PalpaImpactCarousel);
  }
})();
